import { useEffect, useMemo, useState } from "react";
import { useMe } from "@/data/user.ts";
import PageHeading from "@/components/ui/PageHeading.tsx";
import { useSEO } from "@/contexts/SeoContext.tsx";
import useGlobals from "@/utils/hooks/useGlobals.ts";
import DashboardContentLayout from "@/layouts/DashboardContentLayout.tsx";
import { useTranslation } from "react-i18next";
import { useClassReports } from "@/data/class-reports.ts";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select.tsx";
import ClassReportFeedback from "@/components/class-report/FeedbackItem";
import NoItem from "@/components/NoItem.tsx";
import { uuid } from "@/utils/string.ts";
import { ClassReportsItem } from "@/types";
import List from "@edusogno/composites/list";

const filterOptions = [
  { label: "class_report.filter.topic", value: "0" },
  { label: "class_report.filter.tutor", value: "1" },
  { label: "class_report.filter.date", value: "2" },
];

const sortByTopic = (a: ClassReportsItem, b: ClassReportsItem) =>
  a.event?.titolo_argomento?.localeCompare(b.event?.titolo_argomento);
const sortByTutorName = (a: ClassReportsItem, b: ClassReportsItem) =>
  a.tutor.first_name.localeCompare(b.tutor.first_name);
const sortByEventDateDesc = (a: ClassReportsItem, b: ClassReportsItem) =>
  new Date(b.event.data + " " + b.event.ora).getTime() -
  new Date(a.event.data + " " + a.event.ora).getTime();

  export default function ClassReport() {
    const { me } = useMe();
  
    const { setTitle, setDescription } = useSEO();
    const { setGlobalState } = useGlobals();
    const [filter, setFilter] = useState("2"); // Set default filter to date descending
  
    const { reports: allReports, isLoading } = useClassReports();
  
    useEffect(() => {
      setGlobalState("backgroundClass", "bg-brand-background");
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
  
    const { t } = useTranslation();
  
    useEffect(() => {
      setTitle(
        `${me?.item.user.first_name} ${me?.item.user.last_name} | Class Report`,
      );
      setDescription("Class Report");
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [me]);
  
    const sortedReports =
      useMemo(() => {
        if (!allReports) return [];
        let sorted = [...allReports.items];
        switch (filter) {
          case "0":
            sorted.sort(sortByTopic);
            break;
          case "1":
            sorted.sort(sortByTutorName);
            break;
          case "2":
            sorted.sort(sortByEventDateDesc);
            break;
          default:
            sorted = allReports.items;
        }
        return sorted;
      }, [filter, allReports]) || [];
  
    return (
      <DashboardContentLayout loading={isLoading}>
        <PageHeading
          title={t("menu.class_report")}
          subtitleClassName={"text-light"}
          subtitle={t("class_report.sub_header")}
          button={
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger
                showIcon={false}
                className="min-w-[180px] w-full flex items-center bg-light text-sm  border-0 text-brand"
              >
                <SelectValue placeholder={t("class_report.select_filter")} />
              </SelectTrigger>
              <SelectContent>
                {filterOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value.toString()}>
                    <div className={"flex items-center gap-x-2 justify-between"}>
                      <span>{t(option.label)}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
        />
        <List className={"overflow-y-auto pr-0"}>
          <div className="max-w-full w-full mx-auto ">
            <div className="h-full gap-4 w-full">
              <div className="flex flex-col items-center justify-center w-full h-full mt-6 space-y-6">
                {sortedReports && sortedReports.length > 0 ? (
                  sortedReports.map((report) => (
                    <ClassReportFeedback key={uuid()} report={report} />
                  ))
                ) : (
                  <NoItem text={t("class_report.no_report")} />
                )}
              </div>
            </div>
          </div>
        </List>
      </DashboardContentLayout>
    );
  }